﻿/**
 * Represents the planboard.
 */
export class Planboard {
    private moreText: string;

    /**
     * Initializes a new instance of the Planboard class.
     * @param moreText - The text to display when there are more items than can be displayed.
     */
    constructor(moreText: string) {
        this.moreText = moreText;

        window.addEventListener("resize", this.Debounce((e) => {
            this.AdjustGroupItemsVisibility();
        }));
    }

    /**
     * Adjusts the visibility of group items.
     */
    public AdjustGroupItemsVisibility() {
        const dateContents = document.querySelectorAll('.planboard--month__date-content');
        const grid = document.querySelector('.planboard--month__grid') as HTMLElement;

        grid.style.removeProperty('grid-auto-rows');

        // Calculate the minimum height requirement for a day cell.
        const dateHeader = grid.querySelector('.planboard--month__date') as HTMLElement;
        const container = grid.querySelector('.planboard__group-container') as HTMLElement;

        if (container === null) {
            return;
        }

        const groupItem = grid.querySelector('.group-item') as HTMLElement;
        const padding = 4;
        const itemHeight = groupItem !== null ? 29 + padding : 0;
        const headerHeight = 37;

        let overallMinimumHeight = 0;
        dateContents.forEach((dateContent) => {
            let minimumHeight = dateHeader.offsetHeight;

            const groups = dateContent.querySelectorAll('.planboard--month__group');
            groups.forEach((group) => {
                minimumHeight += headerHeight;

                const items = Array.from(group.querySelectorAll('.group-item')) as HTMLElement[];
                if (items.length > 0) {
                    minimumHeight += itemHeight;
                    minimumHeight += padding;
                }
            });

            if (minimumHeight > overallMinimumHeight) {
                overallMinimumHeight = minimumHeight;
            }
        });

        // Set the maximum height needed for all date contents
        if (grid) {
            const currentHeight = (dateContents[0] as HTMLElement).clientHeight;
            const numberOfRows = grid.querySelectorAll('.day').length / 7;

            if (currentHeight < overallMinimumHeight && overallMinimumHeight * numberOfRows > this.GetTotalAvailableSpace()) {
                grid.style.gridAutoRows = `${overallMinimumHeight}px`;
            }
        }

        // Adjust visibility of items based on available height
        dateContents.forEach((dateContent) => {
            const groups = dateContent.querySelectorAll('.planboard--month__group');
            let availableHeight = (dateContent as HTMLElement).clientHeight;

            // Pre-calculate total headers height and reserve space for at least one item per group
            let totalReservedForHeadersAndMinimumItems = 0;
            groups.forEach((group) => {
                totalReservedForHeadersAndMinimumItems += headerHeight;
                const items = group.querySelectorAll('.group-item');
                if (items.length > 0) {
                    totalReservedForHeadersAndMinimumItems += itemHeight + padding; // Reserve space for at least one item
                }
            });

            availableHeight -= totalReservedForHeadersAndMinimumItems;

            // Adjust for each group considering reserved space for upcoming groups
            groups.forEach((group) => {
                // Remove existing "more" items before adding new ones
                const existingMoreItems = group.querySelectorAll('.group-item-more');
                existingMoreItems.forEach(item => item.remove());

                const container = group.querySelector('.planboard__group-container') as HTMLElement;
                const items = Array.from(container.querySelectorAll('.group-item')) as HTMLElement[];
                let groupHeight = items.reduce((total) => total + itemHeight, 0);

                const moreItemPrototype = document.createElement('div'); // For height calculation
                moreItemPrototype.className = 'group-item-more';

                let itemsToShow = items.length;
                // Adjust visibility of items based on adjusted available height
                while (groupHeight > availableHeight && itemsToShow > 0) {
                    itemsToShow--;
                    groupHeight -= itemHeight;
                }

                // Apply visibility changes and add 'more' item if needed
                const remainingItems = items.length - itemsToShow;
                items.forEach((item, index) => {
                    if (index >= itemsToShow && remainingItems > 1) {
                        item.classList.add('hidden');
                    } else {
                        item.classList.remove('hidden');
                        availableHeight -= itemHeight;
                    }
                });

                if (itemsToShow < items.length && remainingItems > 1) {
                    const moreItem = document.createElement('div');
                    moreItem.className = 'group-item group-item-more';
                    moreItem.innerText = `+${items.length - itemsToShow} ${this.moreText}`;
                    container.appendChild(moreItem);
                } else if (itemsToShow > 0) {
                    availableHeight += itemHeight;
                }
            });
        });
    }

    /**
     * Debounces the given function.
     * @param func - The function to debounce.
     */
    private Debounce(func: Function) {
        let timer;
        return function (event) {
            if (timer) clearTimeout(timer);
            timer = setTimeout(func, 100, event);
        };
    }

    /**
     * Gets the total available space for the planboard.
     */
    private GetTotalAvailableSpace(): number {
        const planboard = document.querySelector('.planboard--month') as HTMLElement;
        const header = planboard.querySelector('.day-header-row') as HTMLElement;

        return planboard.offsetHeight - header.offsetHeight;
    }
}

window['InitPlanboard'] = function (moreText: string) {
    var planboard = new Planboard(moreText);
    planboard.AdjustGroupItemsVisibility();
    return planboard;
}